// frontend/src/pages/Partners.js
//
// The public page for the two ways someone else can make money with GlamGo:
// recommend it, or run it.
//
// They are presented side by side and at equal weight because they are not a
// funnel — a salon that wants a referral code is not a lesser version of a shop
// that wants a licence, and pushing everyone toward the more expensive option is
// how a page like this stops being read.
//
// There is no application form. An enquiry here is the start of a commercial
// conversation with terms to agree, not a signup, and a form that implies
// otherwise would collect people's details to tell them "we'll be in touch"
// — which is what the contact page already does, honestly.

import React, { useEffect } from 'react';
import { Link } from '../routing';
import { useTranslation } from 'react-i18next';
import { Check, ArrowRight, Store, KeyRound } from 'lucide-react';
import Navbar from '../components/Navbar';
import PageHero from '../components/PageHero';
import { Reveal } from '../components/Motion';
import { useCompany } from '../utils/useCompany';
import theme from '../theme';

export default function Partners() {
  const { t } = useTranslation();
  const company = useCompany();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const cards = [
    {
      key: 'affiliate',
      icon: <Store size={18} />,
      termsTo: '/partner-terms',
      // The salon writes to us; there is nothing to sign up to yet.
      subject: t('partners.affiliate.title'),
    },
    {
      key: 'license',
      icon: <KeyRound size={18} />,
      termsTo: '/license-terms',
      subject: t('partners.license.title'),
    },
  ];

  return (
    <div style={{ fontFamily: theme.font.body, background: theme.cream, minHeight: '100vh' }}>
      <Navbar />
      <PageHero
        eyebrow={t('partners.eyebrow')}
        title={t('partners.title')}
        subtitle={t('partners.subtitle')}
      />

      <div style={{ maxWidth: 1060, margin: '0 auto', padding: '64px 24px 40px' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 28,
        }}>
          {cards.map((card, i) => (
            <Reveal key={card.key} delay={i * 0.08}>
              <div style={{
                background: theme.white, border: `1px solid ${theme.creamBone}`,
                borderRadius: 4, padding: '38px 34px', height: '100%',
                display: 'flex', flexDirection: 'column',
              }}>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 9, color: theme.gold,
                  fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: 16,
                }}>
                  {card.icon} {t(`partners.${card.key}.label`)}
                </div>

                <h2 style={{
                  fontFamily: theme.font.display, fontSize: 30, fontWeight: 400,
                  color: theme.ink, margin: '0 0 14px', lineHeight: 1.15,
                }}>
                  {t(`partners.${card.key}.title`)}
                </h2>

                <p style={{ color: theme.grey600, fontSize: 15, lineHeight: 1.8, margin: '0 0 26px' }}>
                  {t(`partners.${card.key}.lead`)}
                </p>

                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 32px', flex: 1 }}>
                  {['b1', 'b2', 'b3', 'b4'].map((b) => (
                    <li key={b} style={{
                      display: 'flex', gap: 11, alignItems: 'flex-start', marginBottom: 14,
                      color: theme.grey600, fontSize: 14, lineHeight: 1.7,
                    }}>
                      <Check size={15} color={theme.gold} style={{ marginTop: 4, flexShrink: 0 }} />
                      <span>{t(`partners.${card.key}.${b}`)}</span>
                    </li>
                  ))}
                </ul>

                <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* A mailto with the subject filled in, rather than a form.
                      It reaches a person, it works with no JavaScript, and it
                      does not ask a salon owner to hand over their details
                      before they have decided anything. */}
                  <a
                    href={`mailto:${company.email}?subject=${encodeURIComponent(`GlamGo — ${card.subject}`)}`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      background: theme.ink, color: theme.white, textDecoration: 'none',
                      padding: '14px 26px', borderRadius: 2, fontSize: 12,
                      letterSpacing: '0.13em', textTransform: 'uppercase',
                    }}>
                    {t(`partners.${card.key}.cta`)} <ArrowRight size={14} />
                  </a>
                  <Link to={card.termsTo} style={{ color: theme.gold, fontSize: 13, textDecoration: 'none' }}>
                    {t(`partners.${card.key}.terms`)}
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <p style={{
          textAlign: 'center', color: theme.grey550, fontSize: 14.5, lineHeight: 1.8,
          maxWidth: 620, margin: '56px auto 0',
        }}>
          {t('partners.contactLead')}{' '}
          <Link to="/contact" style={{ color: theme.gold }}>{t('partners.contactCta')}</Link>
        </p>
      </div>
    </div>
  );
}
